import {
  Link as ChakraLink,
  LinkOverlay,
  LinkOverlayProps,
  LinkProps as ChakraLinkProps,
} from "@chakra-ui/react";
import { forwardRef } from "@chakra-ui/system";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { ReactNode } from "react";

export type Link_T = Omit<CustomChakraLinkProps, "href"> &
  Omit<CustomNextLinkProps, "href"> & {
    isOverlay?: boolean;
    href: string | URL;
  };

type CustomChakraLinkProps = ChakraLinkProps & LinkOverlayProps;
type CustomNextLinkProps = Omit<
  NextLinkProps,
  "as" | "href" | "onMouseEnter" | "onClick"
> & {
  href: string;
};

interface InternalLinkProps {
  chakraProps: CustomChakraLinkProps;
  nextProps: NextLinkProps;
  isOverlay?: boolean;
  children: ReactNode;
}

function parseInternalProps({
  href,
  replace,
  scroll,
  shallow,
  prefetch,
  locale,
  isOverlay,
  children,
  passHref,
  ...rest
}: Link_T): InternalLinkProps & { href: string } {
  if (typeof href !== "string" && typeof href !== "object") {
    throw new Error(`Link element given invalid href: ${JSON.stringify(href)}`);
  }

  // Convert the URLRecord into a string, if necessary
  const hrefPath: string = typeof href === "string" ? href : href.href;

  // See https://nextjs.org/docs/api-reference/next/link
  const nextProps: NextLinkProps = {
    href: hrefPath,
    replace,
    scroll,
    shallow,
    // Required to be true for the href to get passed to chakra's link
    passHref: true,
    prefetch,
    locale,
  };

  const chakraProps: CustomChakraLinkProps = {
    href: hrefPath,
    cursor: "pointer",
    _focus: { boxShadow: "none" },
    ...rest,
  };

  return {
    nextProps,
    chakraProps,
    children,
    isOverlay,
    href: hrefPath,
  };
}

/**
 * Decently efficient url-parsing algorithm to determine whether a link is within our domain
 * Notably, it doesn't use RegExp patterns, which increases efficiency significantly.
 */
function isLocalURL(url: string): boolean {
  if (url.startsWith("mailto:")) {
    return false;
  }
  return (
    url === "" ||
    url.startsWith("/") ||
    url.startsWith("#") ||
    url.startsWith("?") ||
    !url.startsWith("http") ||
    url.includes("areahub.com")
  );
}

export const Link = ({ isExternal, ...props }: Link_T) => {
  const { href, ...parsedProps } = parseInternalProps(props);

  // The Next/Link component is designed to provide fast client-side routing for internal links,
  // The component documentation indicates that using it for external links may throw an error:
  // https://github.com/vercel/next.js/blob/master/errors/invalid-href-passed.md

  if (isExternal || !isLocalURL(href)) {
    return (
      <CustomChakraLink {...parsedProps.chakraProps} isExternal>
        {parsedProps.children}
      </CustomChakraLink>
    );
  }
  // Use the next link
  return <NextChakraLink {...parsedProps} />;
};

/**
 * Combines the NextJs `Link` (@link https://chakra-ui.com/docs/navigation/link),
 * the Chakra-UI `Link` (@link https://nextjs.org/docs/api-reference/next/link),
 * and the Chakra-UI `LinkOverlay` (@link https://chakra-ui.com/docs/navigation/link-overlay)
 * components into a single interface, thereby replacing the <a> tag.
 *
 * Can be used as a basic <a> tag, or to wrap entire cards/components.
 * To wrap a cards/component, this component should be a child of a `LinkBox`, and set @param isOverlay = true
 * From @link https://html.spec.whatwg.org/multipage/text-level-semantics.html#the-a-element:
 *  > there must be no interactive content descendant, a element descendant,
 * or descendant with the tabindex attribute specified.
 */
const NextChakraLink = ({
  chakraProps,
  nextProps,
  isOverlay,
  children,
}: InternalLinkProps) => {
  // Choose which type of link based on whether this link will be overlaid with a LinkBox
  const ChakraLinkComponent = isOverlay ? LinkOverlay : CustomChakraLink;
  return (
    /* As seen here: https://chakra-ui.com/docs/navigation/link#usage-with-nextjs */
    <NextLink {...nextProps} legacyBehavior>
      <ChakraLinkComponent {...chakraProps}>{children}</ChakraLinkComponent>
    </NextLink>
  );
};

/**
 * A variant on Chakra's `Link` component.
 * * Does not add `noreferrer` to external links (default for Chakra component)
 * * Makes the cursor a pointer on hover
 *
 * Also satisfies the ref-forwarding required by the NextJs `Link` component with a child functional-component:
 * @link https://nextjs.org/docs/api-reference/next/link#if-the-child-is-a-functional-component
 */
const CustomChakraLink = forwardRef<ChakraLinkProps, "a">((props, ref) => (
  <ChakraLink
    ref={ref}
    {...(props as ChakraLinkProps)}
    rel={props?.isExternal ? "noopener" : undefined}
  />
));
